import {format} from 'date-fns';
import {useState} from 'react';
import {delay} from 'recoil-toolkit';
import {useShallow} from 'zustand/react/shallow';

import {PostFiveStarsParams, RatingStatusEnum} from 'domain/usecases';
import {makePostFiveStars} from 'main/factories/usecases';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useAdditionalQuestionsErrorStore} from '../../stores/useAdditionalQuestionsErrorStore';
import {useMotivatorsStore} from '../../stores/useMotivatorsStore';
import {useRatingErrorStore} from '../../stores/useRatingErrorStore';
import {useRatingFormDataStore} from '../../stores/useRatingFormDataStore';
import {RatingFormData} from '../../stores/useRatingFormDataStore/types';
import {useRatingModalStore} from '../../stores/useRatingModalStore';
import {useRatingValuesStore} from '../../stores/useRatingValuesStore';
import {UseRatingStarsProps} from './types';

export function useRatingStars({ratingDetail}: UseRatingStarsProps) {
	const [step, setStep] = useState(1);
	const [operationState, setOperationState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);

	const isSubmitting = operationState === OperationStateTypesEnum.SUBMITTING;
	const motivator = ratingDetail.values.motivadores.motivadores[step - 1];
	const motivatorId = motivator.id;
	const motivatorsLength = motivator.questoes_3_estrelas_abaixo.length;
	const totalSteps = ratingDetail.values.motivadores.motivadores.length;

	const motivatorsValues = useMotivatorsStore(
		useShallow(state => state.motivatorsValues[motivatorId] || []),
	);
	const setRatingFormData = useRatingFormDataStore(state => state.setRatingFormData);

	const ratingValue = useRatingValuesStore(s => s.ratingValues[motivatorId] ?? null);
	const showMotivators = ratingValue !== null && ratingValue <= 3;

	const setAdditionalQuestionsError = useAdditionalQuestionsErrorStore(
		state => state.setAdditionalQuestionsError,
	);
	const setRatingError = useRatingErrorStore(state => state.setRatingError);
	const ratingModal = useRatingModalStore(state => state.ratingModal);
	const setRatingModal = useRatingModalStore(state => state.setRatingModal);

	const handleUpdateFormData = (): Promise<RatingFormData> => {
		setRatingError(false);
		setAdditionalQuestionsError(false);

		return new Promise(resolve => {
			setRatingFormData(prevValue => {
				let updatedFormData = [...prevValue];
				const currentStepIndex = updatedFormData.findIndex(
					item =>
						item.cdusuario === ratingDetail.keys.cdusuario &&
						item.tipo === ratingDetail.keys.tipo,
				);

				if (currentStepIndex !== -1) {
					updatedFormData = updatedFormData.map((item, index) => {
						if (index === currentStepIndex) {
							const existingIndex = item.avaliacao.findIndex(
								avaliacao => avaliacao.id_motivador === motivatorId.toString(),
							);

							if (existingIndex !== -1) {
								return {
									...item,
									avaliacao: item.avaliacao.map((avaliacao, avaliacaoIndex) => {
										if (avaliacaoIndex === existingIndex) {
											return {
												...avaliacao,
												nota: ratingValue,
												nota_negativa:
													ratingValue && ratingValue > 3
														? undefined
														: motivatorsValues.filter(
																value => value.resposta,
															),
											};
										}
										return avaliacao;
									}),
								};
							}
							return {
								...item,
								avaliacao: [
									...item.avaliacao,
									{
										nota: ratingValue,
										id_motivador: motivatorId.toString(),
										nota_negativa:
											ratingValue && ratingValue > 3
												? undefined
												: motivatorsValues.filter(value => value.resposta),
									},
								],
							};
						}
						return item;
					});
				} else {
					updatedFormData.push({
						cdatendimento: ratingDetail.keys.cdatendimento,
						cdusuario: ratingDetail.keys.cdusuario,
						tipo: ratingDetail.keys.tipo,
						email: ratingDetail.values.email,
						status: ratingDetail.values.status,
						avaliacao: [
							{
								nota: ratingValue,
								id_motivador: motivatorId.toString(),
								nota_negativa:
									ratingValue && ratingValue > 3
										? undefined
										: motivatorsValues.filter(value => value.resposta),
							},
						],
						canal: '',
						dtinsercao: format(new Date(), 'dd-MM-yyyy HH:mm:ss'),
						origem: '',
					});
				}

				resolve(updatedFormData);
				return updatedFormData;
			});
		});
	};

	const handleNextStep = async () => {
		if (!ratingValue) {
			setRatingError(true);
			return;
		}

		if (
			ratingValue <= 3 &&
			motivatorsValues.some(value => {
				if (value.descricao === 'Outros') {
					return false;
				}
				return !value.resposta;
			})
		) {
			setAdditionalQuestionsError(true);
			return;
		}

		const updatedFormData = await handleUpdateFormData();

		if (step === totalSteps) {
			const data = {
				items: updatedFormData.map(item => ({
					...item,
					status: RatingStatusEnum.EVALUATED,
					avaliacao: JSON.stringify(item.avaliacao),
				})),
			};

			await handleSubmitEvaluation(data);
		} else {
			setStep(value => value + 1);
		}
	};

	const handlePreviousStep = () => {
		setRatingError(false);
		if (step === 1) {
			setRatingModal('refuse');
			return;
		}
		setStep(value => value - 1);
	};

	async function handleSubmitEvaluation(data: PostFiveStarsParams) {
		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			await makePostFiveStars().post(data);
			await delay(1000);

			setOperationState(OperationStateTypesEnum.DONE);
			setRatingModal('success');
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			setRatingModal('error');
		}
	}

	return {
		isSubmitting,
		handlePreviousStep,
		handleNextStep,
		motivatorId,
		motivatorsLength,
		totalSteps,
		step,
		motivator,
		showMotivators,
		handleSubmitEvaluation,
		operationState,
		ratingModal,
	};
}
